import { ThreadPrimitive, ComposerPrimitive, MessagePrimitive, useAuiState } from '@assistant-ui/react';
import Typography from '@mui/material/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ReactComponent as Logo } from '../../images/logo-beige.svg';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { AssistentViewSidebar, ViewRoot } from '../../layout/Main/styled';
import {
  AssistantUIProvider,
  useEnsureSession,
  useUpdateChatSession,
  type ChatSupportAction
} from '../../providers/AssistantUIProvider';
import {
  ComposerWrapper,
  ComposerInput,
  ComposerSendButton,
  TermsOfUseLink,
  SchedulingCardWrapper,
  SchedulingButton,
  SchedulingButtonLabel,
  SchedulingSubtitle,
  TypingIndicatorWrapper,
  TypingDot,
  AssistantMessageSchedulingWrapper,
  AssistantDisplayTextBubble,
  AssistantDisplayText,
  AssistantMessageBox,
  ChatContainer,
  ThreadRoot,
  ThreadViewport,
  UserMessageRoot,
  UserMessageBubble,
  UserAvatarBox,
  AssistantMessageRoot,
  AssistantAvatarBox,
  TriagemViewContent,
  TriagemContentCenter,
  BackButtonWrapper
} from './styled';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { saveCurrentStep } from '../../features/stepper/stepper-slice';
import { useDispatch } from 'react-redux';
import { dynamicCustomEventTracking } from '../../lib/gtm';
import { useGetNeedsMutation } from '../../services/needs.service';
import { CircularProgress } from '@mui/material';
import Login from '../../components/pages/Login';
import ScheduleCancel from '../../components/pages/ScheduleCancel';

const WHATSAPP_PATTERN = /^MOSTRAR_WHATSAPP$/;
const MY_ACCOUNT_PATTERN = /^MOSTRAR_MY_ACCOUNT$/;
const LOGIN_PATTERN = /^MOSTRAR_LOGIN$/;
const SCHEDULE_CANCEL_PATTERN = /^MOSTRAR_SCHEDULE_CANCEL$/;
const SCHEDULING_HOST_PATTERN = /^agendamento(-[a-z0-9]+)?\.oya\.care$/;
const SCHEDULING_PATH_PATTERN = /^\/servico(\/|$)/;
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);
const EMPTY_ACTIONS: ChatSupportAction[] = [];

type ClassifiedAction =
  | { kind: 'myAccount' }
  | { kind: 'login' }
  | { kind: 'whatsapp' }
  | { kind: 'scheduling'; url: URL }
  | { kind: 'external'; url: URL }
  | { kind: 'scheduleCancel'; id: string }
  | { kind: 'unsupported' };

const classifyAction = ({ title, action }: ChatSupportAction): ClassifiedAction => {
  if (MY_ACCOUNT_PATTERN.test(action)) return { kind: 'myAccount' };
  if (LOGIN_PATTERN.test(action)) return { kind: 'login' };
  if (WHATSAPP_PATTERN.test(action)) return { kind: 'whatsapp' };
  if (SCHEDULE_CANCEL_PATTERN.test(action)) {
    // o id do agendamento vem no title da ação
    const id = title?.trim();
    return id ? { kind: 'scheduleCancel', id } : { kind: 'unsupported' };
  }

  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return { kind: 'unsupported' };
  }

  // only http(s) links are rendered, blocking javascript:, data: and other executable schemes
  if (!ALLOWED_PROTOCOLS.has(url.protocol)) return { kind: 'unsupported' };

  const isSchedulingHost = SCHEDULING_HOST_PATTERN.test(url.hostname) || url.hostname === window.location.hostname;
  if (isSchedulingHost && SCHEDULING_PATH_PATTERN.test(url.pathname)) return { kind: 'scheduling', url };

  return { kind: 'external', url };
};

const SchedulingLinkCard = ({ title, url, showSubtitle }: { title: string; url: URL; showSubtitle: boolean }) => {
  const params = new URLSearchParams(url.search);
  params.set('utm_source', 'agendamento');
  params.set('utm_medium', 'suporte_virtual');
  const path = `${url.pathname}?${params.toString()}`;

  const handleClick = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'chat_support_click_link_ag' });
  };

  return (
    <SchedulingCardWrapper>
      <SchedulingButton component={Link} to={path} state={{ hasBackButton: true }} onClick={handleClick}>
        <SchedulingButtonLabel variant="body1B">{title}</SchedulingButtonLabel>
        <NorthEastIcon sx={{ fontSize: 24, color: 'brand.darkGreen' }} />
      </SchedulingButton>
      {showSubtitle && (
        <SchedulingSubtitle>
          Você será redirecionada para uma página da Oya Care para continuar seu agendamento.
        </SchedulingSubtitle>
      )}
    </SchedulingCardWrapper>
  );
};

const LoginAction = () => {
  const updateChatSession = useUpdateChatSession();
  return <Login onContinueWithoutLogin={() => updateChatSession()} onSuccess={updateChatSession} />;
};

const AssistantActionItem = ({
  action,
  classified,
  showSchedulingSubtitle
}: {
  action: ChatSupportAction;
  classified: ClassifiedAction;
  showSchedulingSubtitle: boolean;
}) => {
  if (classified.kind === 'myAccount') {
    const link = process.env.REACT_APP_URL_MYACCOUNT || 'https://minhaconta.oya.care/';
    return (
      <Button variant="contained" endIcon={<NorthEastIcon />} href={link} target="_blank" rel="noopener">
        {action.title}
      </Button>
    );
  }

  if (classified.kind === 'whatsapp') {
    return (
      <Button variant="contained" endIcon={<WhatsAppIcon />} href="https://bit.ly/Fale_com_a_Oya" target="_blank">
        {action.title}
      </Button>
    );
  }

  if (classified.kind === 'scheduleCancel') {
    return <ScheduleCancel id={classified.id} />;
  }

  if (classified.kind === 'login') {
    return <LoginAction />;
  }

  if (classified.kind === 'scheduling') {
    return <SchedulingLinkCard title={action.title} url={classified.url} showSubtitle={showSchedulingSubtitle} />;
  }

  if (classified.kind === 'unsupported') return null;

  return (
    <Button variant="contained" endIcon={<NorthEastIcon />} href={classified.url.href} target="_blank" rel="noopener">
      {action.title}
    </Button>
  );
};

const AssistantTypingIndicator = () => (
  <TypingIndicatorWrapper>
    {['0s', '0.2s', '0.4s'].map((delay, i) => (
      <TypingDot key={i} delay={delay} />
    ))}
  </TypingIndicatorWrapper>
);

const AssistantMessageBubble = () => {
  const isRunning = useAuiState(s => s.message?.status?.type === 'running');
  const messageText = useAuiState(s => {
    const textPart = s.message?.content?.find(c => c.type === 'text');
    return textPart && 'text' in textPart ? (textPart as { type: 'text'; text: string }).text : '';
  });
  const actions = useAuiState(
    s => (s.message?.metadata?.custom?.actions as ChatSupportAction[] | undefined) ?? EMPTY_ACTIONS
  );
  const hasContent = messageText.length > 0;

  if (isRunning && !hasContent) return <AssistantTypingIndicator />;

  if (actions.length > 0) {
    const classifiedActions = actions.map(classifyAction);
    const lastSchedulingIndex = classifiedActions.reduce(
      (lastIndex, classified, index) => (classified.kind === 'scheduling' ? index : lastIndex),
      -1
    );

    return (
      <AssistantMessageSchedulingWrapper>
        {messageText && (
          <AssistantDisplayTextBubble>
            <AssistantDisplayText>{messageText}</AssistantDisplayText>
          </AssistantDisplayTextBubble>
        )}
        {actions.map((action, index) => (
          <AssistantActionItem
            key={index}
            action={action}
            classified={classifiedActions[index]}
            showSchedulingSubtitle={index === lastSchedulingIndex}
          />
        ))}
      </AssistantMessageSchedulingWrapper>
    );
  }

  return (
    <AssistantMessageBox>
      <MessagePrimitive.Content />
    </AssistantMessageBox>
  );
};

const UserMessage = () => (
  <UserMessageRoot>
    <UserMessageBubble>
      <MessagePrimitive.Content />
    </UserMessageBubble>
    <UserAvatarBox>
      <SentimentSatisfiedAltIcon sx={{ fontSize: 20, color: 'brand.darkGreen' }} />
    </UserAvatarBox>
  </UserMessageRoot>
);

const AssistantMessage = () => (
  <AssistantMessageRoot>
    <AssistantAvatarBox>
      <Logo />
    </AssistantAvatarBox>
    <AssistantMessageBubble />
  </AssistantMessageRoot>
);

// referência estável: componentes inline seriam recriados a cada render e remontariam as mensagens (perdendo estado, ex: card de Login)
const THREAD_MESSAGE_COMPONENTS = { UserMessage, AssistantMessage };

const VirtualAssistantChat = () => {
  return (
    <AssistantUIProvider>
      <ChatContainer>
        <ThreadRoot>
          <ThreadViewport>
            <ThreadPrimitive.Messages components={THREAD_MESSAGE_COMPONENTS} />
          </ThreadViewport>

          <ComposerPrimitive.Root>
            <ComposerWrapper>
              <ComposerInput placeholder="Como podemos ajudar?" maxLength={450} />
              <ComposerPrimitive.Send asChild>
                <ComposerSendButton component="button">
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                </ComposerSendButton>
              </ComposerPrimitive.Send>
            </ComposerWrapper>
          </ComposerPrimitive.Root>
        </ThreadRoot>
      </ChatContainer>
    </AssistantUIProvider>
  );
};

const VirtualAssistant = () => {
  const navigate = useNavigate();
  const [getNeedsMutation, { data, isLoading }] = useGetNeedsMutation();
  const { isLoading: isSessionLoading } = useEnsureSession();
  const dispatch = useDispatch();
  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    dynamicCustomEventTracking.send({ dynamicEventName: `chat_support_start_ag` });
    dispatch(saveCurrentStep('supportChat'));
    getNeedsMutation('');
  }, []);

  // useEffect(() => {
  //   if (data && !data.supportChatEnabled) {
  //     navigate('/');
  //   }
  // }, [data]);

  if (isLoading || isSessionLoading) {
    return (
      <ViewRoot>
        <TriagemViewContent className="ViewContent">
          <TriagemContentCenter>
            <CircularProgress sx={{ color: 'brand.darkGreen' }} />
          </TriagemContentCenter>
        </TriagemViewContent>
        <AssistentViewSidebar component="aside" className="ViewSidebar">
          <Typography color="neutrals.beige1" variant="h1">
            Assistente digital de agendamento da Oya
          </Typography>
        </AssistentViewSidebar>
      </ViewRoot>
    );
  }

  return (
    <ViewRoot>
      <TriagemViewContent className="ViewContent">
        <TriagemContentCenter>
          <VirtualAssistantChat />
          <BackButtonWrapper>
            <Button variant="inline" color="primary" onClick={goBack} startIcon={<ArrowBackIcon />}>
              Voltar
            </Button>
            <TermsOfUseLink>
              <Typography variant="body1R" sx={{ fontSize: '12px' }}>
                Ao continuar com este bate-papo, você concorda que sua conversa pode ser gravada. As respostas geradas pela
                IA podem incluir erros. Verifique informações importantes.
              </Typography>
            </TermsOfUseLink>
          </BackButtonWrapper>
        </TriagemContentCenter>
      </TriagemViewContent>
      <AssistentViewSidebar component="aside" className="ViewSidebar">
        <Typography color="neutrals.beige1" variant="h1">
          Assistente digital de agendamento da Oya
        </Typography>
      </AssistentViewSidebar>
    </ViewRoot>
  );
};

export default VirtualAssistant;
